/* eslint-disable no-alert */
/* eslint-disable no-unused-expressions */
import {
  Alert,
  Box,
  Button,
  Card,
  Container,
  Skeleton,
  Snackbar,
  SwipeableDrawer,
  TextField,
  Typography,
} from '@mui/material'

import * as yup from 'yup'
import { ReactElement, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchSections,
  fetchTitleScreens,
  selectActiveClass,
  selectSectionsStatus,
  selectTitleScreenByClassID,
  selectTitleScreensStatus,
  fetchClasses,
  selectClassByID,
  selectClassesStatus,
} from 'src/v3/create/create.slice'

import { useFormik, Field, FormikProvider, FieldProps } from 'formik'
import axios from 'src/utilities/http-client'
import { compose, F, pipe, T } from 'ramda'
import { processImageFile, uploadImageToS3 } from 'src/v3/utilities/s3'
import noop from 'src/utilities/noop'
import DangerousHtml from 'src/components/common/dangerous-html'
import Wysiwyg from 'src/components/common/wysiwyg'
import { makeStyles } from '@mui/styles'
import Loading from 'src/v3/components/loading'

const useStyles = makeStyles(() => ({
  overlay: {
    position: 'relative',
  },
  overlayChild: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
    transition: 'backgroundColor 0.3s ease',
    display: 'none',
  },
  overlayHover: {
    '&:hover $overlayChild': {
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'block',
    },
  },
}))

interface FormValues {
  name: string
  description: string
  content: string
}

export default (): ReactElement => {
  let styles = useStyles()
  let dispatch = useAppDispatch()
  let navigate = useNavigate()

  let classesStatus = useAppSelector(selectClassesStatus)
  let sectionsStatus = useAppSelector(selectSectionsStatus)
  let titleScreensStatus = useAppSelector(selectTitleScreensStatus)
  let activeClass = useAppSelector(selectActiveClass)
  let classData = useAppSelector(selectClassByID(activeClass))
  let titleScreen = useAppSelector(selectTitleScreenByClassID(activeClass))

  let [errorMessage, setErrorMessage] = useState<string>('')
  let [saving, setSaving] = useState<boolean>(false)
  let [formOpen, setFormOpen] = useState<boolean>(false)

  let form = useFormik<FormValues>({
    validationSchema: yup.object({
      name: yup.string().required(),
      description: yup.string(),
      content: yup.string().required(),
    }),
    enableReinitialize: true,
    initialValues: {
      name: '',
      description: '',
      content: '',
    },
    onSubmit: (values) => {
      setSaving(true)
      if (!titleScreen?.id) {
        axios
          .post('/api/v2/create/title-screen', {
            name: values.name,
            description: values.description,
            content: values.content,
            classID: activeClass,
          })
          .then(() => dispatch(fetchTitleScreens(activeClass)))
          .then(() => setFormOpen(false))
          .then(() => setSaving(false))
          .then(() => form.resetForm())
          .catch((err) => {
            setErrorMessage(err?.message || 'Error Creating Title Screen')
            dispatch(fetchTitleScreens(activeClass))
            setFormOpen(false)
            setSaving(false)
          })
      } else {
        axios
          .put('/api/v2/create/title-screen', {
            id: titleScreen.id,
            name: values.name,
            description: values.description,
            content: values.content,
          })
          .then(() => dispatch(fetchTitleScreens(activeClass)))
          .then(() => setFormOpen(false))
          .then(() => setSaving(false))
          .then(() => form.resetForm())
          .catch((err) => {
            setErrorMessage(err?.message || 'Error Updating Title Screen')
            dispatch(fetchTitleScreens(activeClass))
            setFormOpen(false)
            setSaving(false)
          })
      }
    },
  })

  useEffect(() => {
    if (sectionsStatus === 'uninitialized') {
      dispatch(fetchSections())
    }
  }, [sectionsStatus])

  useEffect(() => {
    if (classesStatus === 'uninitialized') {
      dispatch(fetchClasses())
    }
  }, [classesStatus])

  useEffect(() => {
    if (
      titleScreensStatus === 'uninitialized' &&
      activeClass !== 'unselected'
    ) {
      dispatch(fetchTitleScreens(activeClass))
    }
  }, [titleScreensStatus, activeClass])

  useEffect(() => {
    if (titleScreensStatus === 'fulfilled' && !titleScreen) {
      dispatch(fetchTitleScreens(activeClass))
    }
  }, [activeClass])

  useEffect(() => {
    if (!formOpen) {
      form.resetForm()
    }
  }, [formOpen])

  useEffect(() => {
    if (activeClass === 'unselected') {
      navigate('/create')
    }
  }, [activeClass])

  let onImageUpload = (id: string, t: any) =>
    axios
      .put('/api/v2/create/title-screen/image', { id })
      .then((response) => uploadImageToS3(t, response.data))
      .then(() => dispatch(fetchTitleScreens(activeClass)))

  let onEdit = () => {
    form.setFieldValue('id', titleScreen?.id)
    form.setFieldValue('name', titleScreen?.name)
    form.setFieldValue('description', titleScreen?.description)
    form.setFieldValue('content', titleScreen?.content)
    setFormOpen(true)
  }

  return (
    <>
      <Box component="main" className="p-4">
        {!titleScreen ? (
          <div className="p-4">
            <Typography
              variant="h4"
              className="my-4 d-inline"
              style={{
                fontFamily: 'smileAndWave',
              }}
            >
              Title Screen
            </Typography>
            <Typography paragraph className="d-inline ml-3">
              Create a title screen for {classData?.name}
            </Typography>
            <div>
              <div className={`${styles.overlay} ${styles.overlayHover} mt-4`}>
                <div className={styles.overlayChild}>
                  <div className="flex flex-col justify-center items-center h-full">
                    <Button
                      variant="contained"
                      onClick={compose(setFormOpen, T)}
                      style={{ width: 300 }}
                    >
                      Add Title Screen
                    </Button>
                  </div>
                </div>
                <Box sx={{ pt: 0.5 }}>
                  <Skeleton
                    className="mt-2"
                    variant="rounded"
                    width="100%"
                    height={50}
                    animation={false}
                  />
                  <Skeleton
                    className="mt-2"
                    variant="rounded"
                    height={25}
                    animation={false}
                  />
                  <Skeleton
                    className="mt-2"
                    variant="rounded"
                    width="100%"
                    height={400}
                    animation={false}
                  />
                </Box>
              </div>
            </div>
          </div>
        ) : (
          <div
            className={`${styles.overlay} ${styles.overlayHover}`}
            style={{
              backgroundImage: `url(${titleScreen?.imageUrl})`,
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
            }}
          >
            <div className={styles.overlayChild}>
              <div className="flex flex-col justify-center items-center h-full">
                <Button
                  variant="contained"
                  onClick={pipe(onEdit, compose(setFormOpen, T))}
                  style={{ width: 300 }}
                >
                  Edit Title Screen
                </Button>
                <Button
                  variant="contained"
                  className="mt-4"
                  component="label"
                  style={{ width: 300 }}
                >
                  Upload Background Image
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    hidden
                    onChange={(event) =>
                      processImageFile(event, {
                        onLoadingStart: noop,
                        onLoadingEnd: (t: any) => {
                          onImageUpload(titleScreen?.id ?? '', t)
                        },
                      })
                    }
                  />
                </Button>
              </div>
            </div>
            <div className="p-4">
              <Typography sx={{ fontFamily: 'smileAndWave' }} variant="h4">
                {classData?.name}
              </Typography>
              <div className="mt-2">
                <DangerousHtml
                  htmlVal={titleScreen?.content ?? ''}
                  isEncoded={true}
                />
              </div>
            </div>
          </div>
        )}
        <SwipeableDrawer
          anchor="bottom"
          open={formOpen && !saving}
          onOpen={compose(setFormOpen, T)}
          onClose={compose(setFormOpen, F)}
          style={{ zIndex: 2000 }}
        >
          <Container maxWidth="lg">
            <Card className="m-4 vh-full overflow-auto">
              <FormikProvider value={form}>
                <form className="p-4" onSubmit={form.handleSubmit}>
                  <TextField
                    fullWidth
                    className="mt-4"
                    variant="standard"
                    value={form.values.name}
                    label="Name"
                    name="name"
                    onChange={form.handleChange}
                    error={form.touched.name && !!form.errors.name}
                  />

                  <TextField
                    fullWidth
                    className="my-4"
                    variant="standard"
                    value={form.values.description}
                    label="Description"
                    name="description"
                    onChange={form.handleChange}
                    error={
                      form.touched.description && !!form.errors.description
                    }
                  />

                  {document && (
                    <Field name="content">
                      {({ field }: FieldProps) => (
                        <Wysiwyg
                          value={form.values.content}
                          onChange={field.onChange(field.name)}
                        />
                      )}
                    </Field>
                  )}
                  <DangerousHtml
                    htmlVal={form.values.content}
                    isEncoded={true}
                  />

                  <div className="flex flex-row-reverse">
                    <Button
                      variant="contained"
                      color="primary"
                      className="mt-4 mr-3"
                      type="submit"
                    >
                      Submit
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      className="mt-4 mr-3"
                      onClick={compose(setFormOpen, F)}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </FormikProvider>
            </Card>
          </Container>
        </SwipeableDrawer>
      </Box>
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      <Loading
        entities={[classesStatus, sectionsStatus, titleScreensStatus]}
        loading={saving}
      />
    </>
  )
}
